import { z } from 'zod';
import { baseComponentSchema, buttonSchema } from '../component-base.schemas';

// Testimonials component schemas
export const testimonialItemSchema = z.object({
  id: z.string().uuid('Invalid testimonial item ID'),
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  role: z.string().max(100, 'Role must be less than 100 characters').optional(),
  company: z.string().max(100, 'Company must be less than 100 characters').optional(),
  quote: z
    .string()
    .min(1, 'Quote is required')
    .max(1000, 'Quote must be less than 1000 characters'),
  rating: z.number().int().min(1).max(5).optional(),
  avatarUrl: z.string().url().or(z.literal('')).optional(),
  avatarAlt: z.string().max(200, 'Avatar alt text must be less than 200 characters').optional(),
  order: z.number().int().min(0).optional(),
});

export const testimonialsCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    subtitle: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    cardBackground: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    cardBorder: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    quoteText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    nameText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    roleText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    starColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const testimonialsDataSchema = z.object({
  title: z
    .string()
    .min(1, 'Testimonials section title is required')
    .max(200, 'Title must be less than 200 characters'),
  subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').optional(),
  items: z.array(testimonialItemSchema).min(1, 'At least one testimonial is required'),
  layout: z.enum(['grid', 'carousel', 'list']).default('grid'),
  showRating: z.boolean().default(true),
  showAvatar: z.boolean().default(true),
  showCompany: z.boolean().default(true),
  customColors: testimonialsCustomColorsSchema,
  // Optional call-to-action buttons
  buttons: z.array(buttonSchema).optional(),
});

export const testimonialsComponentSchema = baseComponentSchema.extend({
  type: z.literal('testimonials'),
  data: testimonialsDataSchema,
});

// Type exports
export type TestimonialItem = z.infer<typeof testimonialItemSchema>;
export type TestimonialsData = z.infer<typeof testimonialsDataSchema>;
export type TestimonialsComponent = z.infer<typeof testimonialsComponentSchema>;
